import { useState } from "react";
import { seo } from "@/lib/seo";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { BrandWordmark } from "@/components/app/brand-wordmark";
import logo from "@/assets/quantum-mind-brain.png.asset.json";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  head: () =>
    seo({
      title: "Reset Password — The Quantum Minds",
      description: "Set a new password for your The Quantum Minds study account.",
      noindex: true,
    }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    setBusy(true);
    const result = await updatePassword(password);
    setBusy(false);
    if (!result.ok) return setError(result.message ?? "Could not update your password.");
    setDone(true);
    setTimeout(() => navigate({ to: "/", replace: true }), 900);
  };

  return (
    <div className="aurora flex min-h-[100dvh] items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="glass w-full max-w-sm rounded-[2rem] p-8 text-center"
      >
        <img
          src={logo.url}
          alt="The Quantum MIND logo"
          width={64}
          height={64}
          decoding="async"
          className="mx-auto size-16 object-contain drop-shadow-[0_0_30px_rgba(120,90,255,0.45)]"
        />
        <div className="mt-6 flex justify-center">
          <BrandWordmark />
        </div>
        <h1 className="mt-5 text-lg font-semibold tracking-tight">Set a new password</h1>

        <form onSubmit={submit} className="mt-6 space-y-3 text-left">
          <label className="glass flex items-center gap-3 rounded-2xl px-4 py-3.5">
            <Lock className="size-4 shrink-0 text-muted-foreground" />
            <input
              type="password"
              autoComplete="new-password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </label>
          <button
            type="submit"
            disabled={busy || done}
            className="flex h-14 w-full items-center justify-center rounded-2xl bg-primary text-base font-semibold text-primary-foreground transition-transform active:scale-[0.97] disabled:opacity-70"
          >
            {busy ? <Loader2 className="size-5 animate-spin" /> : done ? "Password updated" : "Update password"}
          </button>
        </form>

        {error ? <p className="mt-5 text-sm text-destructive">{error}</p> : null}
      </motion.div>
    </div>
  );
}
